import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useUITranslation } from '../../hooks';
import Map, { MapRef } from 'react-map-gl/mapbox';
import { UIFormInput, UIFormInputRef } from '../ui-form-input';
import { UIButton } from '../ui-button';
import { UIIcon, UIText, UIToast } from '../..';
import { charcoal, concrete } from '../../styles';
import { MAPBOX_TOKEN } from '@/config';

const ZOOM = 17;

type UILocationPickerProps = {
    defaultLat?: number;
    defaultLng?: number;
    showErrors?: boolean;
    onLocationChange?: (lat: number, lng: number, isValid: boolean) => void;
};
export function UILocationPicker(props: UILocationPickerProps): ReactElement {
    const { t } = useUITranslation();
    const {
        defaultLat,
        defaultLng,
        showErrors = false,
        onLocationChange
    } = props;

    const mapRef = useRef<MapRef>(null);
    function centerMapOnLocationIfValid(lat: number, lng: number) {
        if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
            mapRef.current?.setCenter([lng, lat]);
        }
    }

    /**
     * Lat
     */
    const latRef = useRef<UIFormInputRef>(null);
    const [lat, setLat] = useState(defaultLat?.toFixed(5) ?? '');
    const [latFocused, setLatFocused] = useState(false);
    const [latError, setLatError] = useState('');
    const checkLat = useCallback(
        (lat: string): string => {
            const latTrimmed = lat.trim();
            // Check if it's a valid lat
            // 1. Cast to number
            // 2. Check if it's a number
            // 3. Check if it's between -90 and 90
            const latNumber = Number(latTrimmed);
            if (isNaN(latNumber)) {
                return t(
                    'components.ui_location_picker.latitude_error_not_a_number'
                );
            } else if (latNumber < -90 || latNumber > 90) {
                return 'Latitude must be between -90 and 90';
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showErrors) {
            setLatError(checkLat(lat));
        }
    }, [showErrors, lat, checkLat]);

    /**
     * Lng
     */
    const lngRef = useRef<UIFormInputRef>(null);
    const [lng, setLng] = useState(defaultLng?.toFixed(5) ?? '');
    const [lngFocused, setLngFocused] = useState(false);
    const [lngError, setLngError] = useState('');
    const checkLng = useCallback(
        (lng: string): string => {
            const lngTrimmed = lng.trim();
            // Check if it's a valid lat
            // 1. Cast to number
            // 2. Check if it's a number
            // 3. Check if it's between -180 and 180
            const lngNumber = Number(lngTrimmed);
            if (isNaN(lngNumber)) {
                return t(
                    'components.ui_location_picker.longitude_error_not_a_number'
                );
            } else if (lngNumber < -180 || lngNumber > 180) {
                return 'Longitude must be between -180 and 180';
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showErrors) {
            setLngError(checkLng(lng));
        }
    }, [showErrors, lng, checkLng]);

    const isLatLngFocused = latFocused || lngFocused;

    function setLocation(lat: string, lng: string, focus = true) {
        setLat(lat);
        setLng(lng);
        if (focus) {
            centerMapOnLocationIfValid(Number(lat), Number(lng));
            mapRef.current?.setZoom(ZOOM);
        }

        let latNum = Number(lat);
        let lngNum = Number(lng);

        if (isNaN(latNum)) {
            latNum = 0;
        }
        if (isNaN(lngNum)) {
            lngNum = 0;
        }

        onLocationChange?.(
            latNum,
            lngNum,
            checkLat(lat) === '' && checkLng(lng) === ''
        );
    }

    function processGPSCoordinateValue(value: string): string {
        let processedValue = value;

        // Remove all characters except numbers, dots and minus
        processedValue = processedValue.replace(/[^\d.-]/g, '');

        // Remove all dots after the first one. Keep the first
        processedValue = processedValue.replace(/\.(?=.*\.)/g, '');

        // Remove minus if it's not the first character or if there are multiple minuses
        processedValue = processedValue.replace(/^-{2,}/g, '-');

        return processedValue;
    }

    const [getLocationLoading, setGetLocationLoading] = useState(false);
    function getLocation() {
        if (navigator.geolocation) {
            setGetLocationLoading(true);
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    setLocation(latitude.toFixed(5), longitude.toFixed(5));
                    setGetLocationLoading(false);
                },
                (err) => {
                    UIToast.error(err.message);
                    setGetLocationLoading(false);
                }
            );
        } else {
            UIToast.error('Geolocation is not supported by this browser.');
        }
    }

    return (
        <div style={{ display: 'flex', gap: 16, flexDirection: 'column' }}>
            <div style={{ display: 'flex', gap: 16 }}>
                <div
                    style={{
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'column',
                        gap: 16
                    }}
                >
                    <UIFormInput
                        ref={latRef}
                        name="lat"
                        label={'Latitude'}
                        value={lat}
                        type="text"
                        autoComplete="on"
                        errorText={showErrors ? latError : ''}
                        required={false}
                        maxLength={1000}
                        onChange={(_value) => {
                            const value = processGPSCoordinateValue(_value);
                            setLocation(value, lng);
                        }}
                        onFocusChange={setLatFocused}
                    />

                    <UIFormInput
                        ref={lngRef}
                        name="lng"
                        label={'Longitude'}
                        value={lng}
                        type="text"
                        autoComplete="on"
                        errorText={showErrors ? lngError : ''}
                        required={false}
                        maxLength={1000}
                        onChange={(_value) => {
                            const value = processGPSCoordinateValue(_value);
                            setLocation(lat, value);
                        }}
                        onFocusChange={setLngFocused}
                    />

                    <UIButton
                        type="button"
                        leftIcon="crosshair"
                        loading={getLocationLoading}
                        onClick={() => {
                            getLocation();
                        }}
                    >
                        {t(
                            'components.ui_location_picker.use_current_location'
                        )}
                    </UIButton>
                </div>
                <div style={{ position: 'relative', display: 'flex', flex: 1 }}>
                    <Map
                        ref={mapRef}
                        style={{
                            width: '100%',
                            height: '100%',
                            borderRadius: 8,
                            borderWidth: 1,
                            borderColor: concrete
                        }}
                        mapStyle="mapbox://styles/mapbox/streets-v12"
                        projection={{ name: 'mercator' }}
                        mapboxAccessToken={MAPBOX_TOKEN}
                        initialViewState={{
                            latitude: defaultLat,
                            longitude: defaultLng,
                            zoom: ZOOM
                        }}
                        onMove={(event) => {
                            if (!isLatLngFocused) {
                                setLocation(
                                    event.viewState.latitude.toFixed(5),
                                    event.viewState.longitude.toFixed(5),
                                    false
                                );
                            }
                        }}
                        scrollZoom={{ around: 'center' }}
                    />
                    <div
                        style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            pointerEvents: 'none'
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <UIIcon name="map-pin" size={16} color={charcoal} />
                            <UIIcon
                                name="map-pin"
                                size={16}
                                color={'rgba(0,0,0,0)'}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ display: 'flex', gap: 16 }}>
                <div style={{ display: 'flex', flex: 1 }}>
                    <UIText variant="small" type="default2">
                        Drag the map to position the beacon or enter the
                        coordinates directly
                    </UIText>
                </div>
            </div>
        </div>
    );
}
